import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { DemoProjectsCloneDialog } from './demo-projects-clone-dialog.js';

import type { HilProjectPreview } from '@cadstrom/backend/shared';

export interface DemoProjectsCloneControlProps {
	/** The organization (by name) currently being viewed. */
	orgName: string;
	/** The org's current projects, used to detect already-added demos. */
	projects: HilProjectPreview[];
	className?: string;
}

/**
 * Staff-only control at the bottom of the demo section: an "Add demo projects"
 * button that opens the clone picker, plus a live banner while a clone batch is
 * running for this org (server-truth, so it shows for any staff viewer). Renders
 * nothing for non-staff users.
 */
export function DemoProjectsCloneControl({ orgName, projects, className }: DemoProjectsCloneControlProps) {
	const trpc = useTRPC();
	const [dialogOpen, setDialogOpen] = useState(false);

	const ability = useAbilityContext();
	const isStaff = ability.can('manage', 'devTools');

	const { data: seedStatus } = useQuery({
		...trpc.organization.getDemoSeedStatusForOrg.queryOptions({ organizationName: orgName }),
		enabled: isStaff,
		refetchInterval: (query) => (query.state.data?.status === 'running' ? 1500 : false),
	});

	if (!isStaff) {
		return null;
	}

	const isRunning = seedStatus?.status === 'running';
	const remaining = seedStatus ? seedStatus.total - seedStatus.completed : 0;

	return (
		<div className={clsx('flex items-center gap-3 border-t border-gray-a4 pt-3', className)}>
			<Button variant='outlined' startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
				<span>Add demo projects</span>
			</Button>
			{isRunning && (
				<span className='flex items-center gap-2 text-sm text-gray-a11'>
					<CircularProgress size={14} />
					Cloning {remaining} demo project{remaining === 1 ? '' : 's'}…
				</span>
			)}
			<DemoProjectsCloneDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				orgName={orgName}
				projects={projects}
			/>
		</div>
	);
}
